import React, {useEffect, useState} from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid2,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {ArrowBack, Delete, Flight, FlightTakeoff, PersonAdd, Tag} from '@mui/icons-material';
import {t} from 'ttag';
import {DateTime} from 'luxon';
import {useLocation} from 'react-router-dom';
import {IFlight, IPassenger} from '../../store/apis/interfaces/flight';
import useFadeNavigate from './useFadeNavigate';
import FlightStatusBadge from './FlightStatusBadge';

const fmt = (iso: string | null) =>
  iso ? DateTime.fromISO(iso).toFormat('HH:mm') : '—';

const ataStaMinutes = (flight: IFlight): number => {
  const sta = flight.arrival?.scheduledTime;
  const ata = flight.arrival?.actualTime;
  if (!sta || !ata) return 0;
  return Math.max(0, Math.round(DateTime.fromISO(ata).diff(DateTime.fromISO(sta), 'minutes').minutes));
};

interface LocationState {
  flight: IFlight;
  carrier: string;
  flightNumber: string;
  date: string;
  origin: string;
}

const emptyForm = {paxGivenName: '', paxSurname: '', pnr: ''};

const SATA_BLUE = '#0082BB';
const SATA_BLUE_DARK = '#005A8E';

const CancellationPassengersPage = () => {
  const navigate = useFadeNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const storageKey = state ? `cancelpax-${state.carrier}-${state.flightNumber}-${state.date}` : null;

  const [passengers, setPassengers] = useState<IPassenger[]>(() => {
    if (!storageKey) return [];
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({paxGivenName: false, paxSurname: false});

  useEffect(() => {
    if (!storageKey) return;
    sessionStorage.setItem(storageKey, JSON.stringify(passengers));
  }, [passengers]);

  if (!state?.flight) {
    return (
      <Box sx={{textAlign: 'center', mt: 4}}>
        <Typography color='error' gutterBottom>
          {t`No flight data. Please go back and search again.`}
        </Typography>
        <Button
          variant='text'
          startIcon={<ArrowBack />}
          onClick={() => navigate('/declarations/flight')}
          sx={{mt: 1, color: 'text.secondary'}}
        >
          {t`Back to Search`}
        </Button>
      </Box>
    );
  }

  const {flight, carrier, flightNumber, date} = state;
  const formattedDate = DateTime.fromFormat(date, 'yyyy-MM-dd').toLocaleString(DateTime.DATE_FULL);
  const hasDelay = ataStaMinutes(flight) > 0;

  const handleAdd = () => {
    const errors = {
      paxGivenName: !form.paxGivenName.trim(),
      paxSurname: !form.paxSurname.trim()
    };
    setFormErrors(errors);
    if (errors.paxGivenName || errors.paxSurname) return;

    const newPassenger: IPassenger = {
      paxGivenName: form.paxGivenName.trim(),
      paxSurname: form.paxSurname.trim() || null,
      pnr: form.pnr.trim(),
      seatNumber: null,
      ticketNumber: null,
      couponNumber: null,
      flyerId: null,
      destination: null,
      boardingTime: null
    };

    setPassengers((prev) => [...prev, newPassenger]);
    setForm(emptyForm);
    setFormErrors({paxGivenName: false, paxSurname: false});
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); handleAdd(); }
  };

  const removePassenger = (idx: number) => {
    setPassengers((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
      {/* Flight header */}
      <Card elevation={2} sx={{borderTop: '3px solid', borderColor: 'error.main'}}>
        <CardContent sx={{py: 2, '&:last-child': {pb: 2}}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
            <Button
              size='small'
              variant='text'
              onClick={() => navigate('/declarations/flight')}
              sx={{color: 'text.secondary', minWidth: 0, p: 0.5}}
            >
              <ArrowBack />
            </Button>
            <Box sx={{
              background: 'linear-gradient(135deg, #0288d1 0%, #01579b 100%)',
              color: 'white',
              borderRadius: 2,
              width: 48,
              height: 48,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 3px 10px rgba(1,87,155,0.45)',
              gap: 0.25,
            }}>
              <Typography sx={{fontWeight: 900, fontSize: '0.9rem', letterSpacing: '0.1em', lineHeight: 1}}>
                {carrier.toUpperCase()}
              </Typography>
              <FlightTakeoff sx={{fontSize: '0.65rem', opacity: 0.75}} />
            </Box>
            <Box sx={{flex: 1, minWidth: 0}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                <Typography variant='h6' fontWeight='bold' lineHeight={1.2}>
                  {carrier.toUpperCase()}{flightNumber}
                </Typography>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                  <Typography variant='body1' fontWeight='bold'>{flight.origin}</Typography>
                  <Flight sx={{fontSize: '1rem', color: 'text.secondary'}} />
                  <Typography variant='body1' fontWeight='bold'>{flight.destination}</Typography>
                </Box>
                <FlightStatusBadge status={flight.status} hasDelay={hasDelay} />
              </Box>
              <Typography variant='body2' color='text.secondary'>
                {formattedDate}
                {flight.departure.scheduledTime && ` · STD: ${fmt(flight.departure.scheduledTime)}`}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Main card */}
      <Card elevation={3}>
        <CardContent sx={{p: 3}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
            <PersonAdd sx={{color: 'text.secondary'}} />
            <Typography variant='h6' fontWeight='bold'>{t`Add Passengers`}</Typography>
          </Box>
          <Typography variant='body2' color='text.secondary' sx={{mb: 3}}>
            {t`Add the passengers to include in the cancellation declaration.`}
          </Typography>

          {/* Add form */}
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2.5,
              mb: 3,
              bgcolor: 'grey.50',
            }}
          >
            {/* Required fields */}
            <Typography
              variant='caption'
              sx={{fontWeight: 700, letterSpacing: '0.07em', color: 'text.secondary', display: 'block', mb: 1.25}}
            >
              {t`PASSENGER NAME`}
            </Typography>
            <Grid2 container spacing={1.5}>
              <Grid2 size={6}>
                <TextField
                  label={t`First Name`}
                  value={form.paxGivenName}
                  onChange={(e) => {
                    setForm((f) => ({...f, paxGivenName: e.target.value}));
                    setFormErrors((fe) => ({...fe, paxGivenName: false}));
                  }}
                  onKeyDown={handleKeyDown}
                  error={formErrors.paxGivenName}
                  helperText={formErrors.paxGivenName ? t`Required` : ''}
                  fullWidth
                  size='small'
                  autoComplete='off'
                />
              </Grid2>
              <Grid2 size={6}>
                <TextField
                  label={t`Last Name`}
                  value={form.paxSurname}
                  onChange={(e) => {
                    setForm((f) => ({...f, paxSurname: e.target.value}));
                    setFormErrors((fe) => ({...fe, paxSurname: false}));
                  }}
                  onKeyDown={handleKeyDown}
                  error={formErrors.paxSurname}
                  helperText={formErrors.paxSurname ? t`Required` : ''}
                  fullWidth
                  size='small'
                  autoComplete='off'
                />
              </Grid2>
            </Grid2>

            {/* Optional divider */}
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, my: 2}}>
              <Divider sx={{flex: 1}} />
              <Typography variant='caption' sx={{fontWeight: 700, letterSpacing: '0.08em', color: 'text.disabled', flexShrink: 0}}>
                {t`OPTIONAL`}
              </Typography>
              <Divider sx={{flex: 1}} />
            </Box>

            {/* Optional fields */}
            <Grid2 container spacing={1.5}>
              <Grid2 size={12}>
                <TextField
                  label='PNR'
                  value={form.pnr}
                  onChange={(e) => setForm((f) => ({...f, pnr: e.target.value.toUpperCase()}))}
                  onKeyDown={handleKeyDown}
                  fullWidth
                  size='small'
                  autoComplete='off'
                  placeholder='ABC123'
                  inputProps={{style: {fontFamily: 'monospace', letterSpacing: '0.06em'}}}
                  InputProps={{
                    startAdornment: <Tag sx={{fontSize: '1rem', color: 'text.disabled', mr: 0.5}} />,
                  }}
                />
              </Grid2>
            </Grid2>

            <Button
              variant='contained'
              color='primary'
              fullWidth
              startIcon={<PersonAdd />}
              onClick={handleAdd}
              sx={{mt: 2.5, fontWeight: 600, borderRadius: 1.5}}
            >
              {t`Add Passenger`}
            </Button>
          </Box>

          {/* Passenger list */}
          {passengers.length > 0 ? (
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
              <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 0.5}}>
                <Button
                  variant='contained'
                  color='primary'
                  size='small'
                  sx={{fontWeight: 600}}
                  onClick={() =>
                    navigate('/declarations/flight/declaration/cancellation', {
                      state: {flight, carrier, flightNumber, date, selectedPassengers: passengers}
                    })
                  }
                >
                  {t`Continue`}
                </Button>
              </Box>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
                <Typography variant='caption' color='text.secondary' sx={{fontWeight: 700, letterSpacing: '0.08em'}}>
                  {passengers.length === 1 ? t`1 PASSENGER ADDED` : `${passengers.length} ${t`PASSENGERS ADDED`}`}
                </Typography>
                <Box sx={{flex: 1, height: '1px', bgcolor: 'divider'}} />
              </Box>
              {[...passengers]
                .sort((a, b) => {
                  const sA = (a.paxSurname ?? '').toLowerCase();
                  const sB = (b.paxSurname ?? '').toLowerCase();
                  if (sA !== sB) return sA.localeCompare(sB);
                  return a.paxGivenName.toLowerCase().localeCompare(b.paxGivenName.toLowerCase());
                })
                .map((p, sortedIdx) => {
                  const initials = [p.paxGivenName?.[0], p.paxSurname?.[0]].filter(Boolean).join('').toUpperCase() || '?';
                  return (
                    <Card
                      key={`${p.paxSurname}-${p.paxGivenName}-${sortedIdx}`}
                      variant='outlined'
                      sx={{
                        borderRadius: 2,
                        borderColor: 'rgba(0,130,187,0.25)',
                        transition: 'all 0.15s ease',
                        '&:hover': {borderColor: 'rgba(0,130,187,0.6)', boxShadow: 'rgba(0,130,187,0.12) 0 2px 8px'},
                      }}
                    >
                      <CardContent sx={{py: 1.5, px: 2, '&:last-child': {pb: 1.5}}}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                          {/* Avatar */}
                          <Box sx={{
                            width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                            background: `linear-gradient(135deg, ${SATA_BLUE} 0%, ${SATA_BLUE_DARK} 100%)`,
                            color: 'white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 700, fontSize: '0.82rem',
                            boxShadow: `0 2px 6px ${SATA_BLUE_DARK}50`,
                          }}>
                            {initials}
                          </Box>

                          {/* Name + meta */}
                          <Box sx={{flex: 1, minWidth: 0}}>
                            <Typography variant='body2' fontWeight={700} noWrap>
                              {p.paxSurname}, {p.paxGivenName}
                            </Typography>
                            {p.pnr && (
                              <Typography variant='caption' color='text.secondary' sx={{fontFamily: 'monospace', letterSpacing: '0.02em'}}>
                                {`PNR ${p.pnr}`}
                              </Typography>
                            )}
                          </Box>

                          {/* Remove */}
                          <Tooltip title={t`Remove`}>
                            <IconButton
                              size='small'
                              onClick={() => removePassenger(passengers.indexOf(p))}
                              sx={{
                                color: 'text.disabled',
                                '&:hover': {color: 'error.main', bgcolor: 'error.50'},
                              }}
                            >
                              <Delete fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
            </Box>
          ) : (
            <Box sx={{
              textAlign: 'center',
              py: 5,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 2,
            }}>
              <Box sx={{
                width: 52, height: 52, borderRadius: '50%',
                bgcolor: 'grey.100',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                mx: 'auto', mb: 1.5,
              }}>
                <PersonAdd sx={{fontSize: '1.6rem', color: 'text.disabled'}} />
              </Box>
              <Typography variant='body2' fontWeight={500} color='text.secondary'>
                {t`No passengers added yet`}
              </Typography>
              <Typography variant='caption' color='text.disabled'>
                {t`Fill in the form above and click Add Passenger`}
              </Typography>
            </Box>
          )}
        </CardContent>

        <Divider />

        <Box sx={{display: 'flex', justifyContent: 'space-between', p: 2}}>
          <Button
            variant='text'
            startIcon={<ArrowBack />}
            onClick={() => navigate('/declarations/flight')}
            sx={{color: 'text.secondary'}}
          >
            {t`Back to Search`}
          </Button>
          <Button
            variant='contained'
            color='primary'
            disabled={passengers.length === 0}
            sx={{fontWeight: 600}}
            onClick={() =>
              navigate('/declarations/flight/declaration/cancellation', {
                state: {
                  flight,
                  carrier,
                  flightNumber,
                  date,
                  selectedPassengers: passengers
                }
              })
            }
          >
            {t`Continue`}
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

export default CancellationPassengersPage;
